'use client'

import { useCallback, useMemo } from 'react'

import { useCart } from 'modules/cart'
import { findDefaultInvoiceAddress, type InvoiceAddressPort } from 'modules/customer'
import { FormMode } from 'shared/types/forms'

import { useCartInvoiceSelection } from './hooks/useCartInvoiceSelection'

export const useInvoiceAddressAdapter = (): InvoiceAddressPort => {
  const cart = useCart()
  const {
    invoiceData,
    setInvoiceData,
    addresses,
    detachDeliveryOrInvoiceAddressFromCart
  } = useCartInvoiceSelection()

  const onInvoiceAddressSaved = useCallback(
    (addressId: string, mode: FormMode) => {
      if (mode === FormMode.edit && addressId !== cart?.invoiceDataId) return

      void setInvoiceData({
        wantsInvoice: true,
        selectedAddressId: addressId
      })
    },
    [cart?.invoiceDataId, setInvoiceData]
  )

  const onInvoiceAddressRemoved = useCallback(
    async (addressId: string) => {
      if (addressId !== invoiceData.selectedAddressId) return

      await detachDeliveryOrInvoiceAddressFromCart()
      const freshAddresses = addresses.filter(({ id }) => id !== addressId)
      const newDefaultAddressId = findDefaultInvoiceAddress(freshAddresses)

      if (newDefaultAddressId) {
        await setInvoiceData(prev => ({
          ...prev,
          selectedAddressId: newDefaultAddressId
        }))
      }
    },
    [
      invoiceData.selectedAddressId,
      detachDeliveryOrInvoiceAddressFromCart,
      addresses,
      setInvoiceData
    ]
  )

  return useMemo(
    () => ({ onInvoiceAddressSaved, onInvoiceAddressRemoved }),
    [onInvoiceAddressSaved, onInvoiceAddressRemoved]
  )
}
